import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector }              from 'react-redux';
import { Link }                                  from 'react-router-dom';
import { Autocomplete, Box, TextField, useTheme, useMediaQuery } from '@mui/material';
import { LocalizationProvider }                  from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker }                            from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs }                          from '@mui/x-date-pickers/AdapterDayjs';
import dayjs                                     from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';

import { Get_All_Sales_By_TeamLeader_Email_Action } from '../Redux/Actions/SalesAction';
import { useSidebarWidth }                          from '../Utility/useSidebarWidth';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/* ─── Design tokens ──────────────────────────────────────────────────────── */
const C = {
  bg:         '#F7F8FC',
  white:      '#FFFFFF',
  border:     '#E8EAF0',
  accent:     '#0D47A1',
  accentDark: '#0a3880',
  accentSoft: 'rgba(13,71,161,0.08)',
  text:       '#1C2340',
  textMuted:  '#7B829A',
  warning:    '#F79009',
  shadow:     '0 1px 3px rgba(16,24,40,0.08)',
  shadowMd:   '0 4px 16px rgba(16,24,40,0.10)',
};

const SIDEBAR_COLLAPSED = 64;

/* ─── Stage colours palette ──────────────────────────────────────────────── */
const STAGE_COLORS = [
  '#0D47A1','#1565C0','#1976D2','#42A5F5','#90CAF9',
  '#F79009','#12B76A','#F04438','#8B5CF6','#06B6D4',
];

/* ─── Custom Recharts Tooltip ────────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontFamily: "'DM Sans',sans-serif" }}>
      <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: payload[0]?.fill || C.accent }}>
        {payload[0]?.value} <span style={{ fontSize: 12, fontWeight: 500, color: C.textMuted }}>leads</span>
      </p>
    </div>
  );
};

/* ─── Stat mini card ─────────────────────────────────────────────────────── */
const MiniStat = ({ label, value, color }) => (
  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', boxShadow: C.shadow, minWidth: 120 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6, fontFamily: "'DM Sans',sans-serif" }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: color || C.accent, lineHeight: 1, fontFamily: "'DM Sans',sans-serif" }}>{value}</div>
  </div>
);

/* ─── Date picker styles ─────────────────────────────────────────────────── */
const dpSx = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: C.white,
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.accent },
    '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: C.accent },
};

/* ════════════════════════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════════════════════ */
export const SalesForTeamLeaderWithDiffrentDates = () => {
  const dispatch   = useDispatch();
  const isDark     = useTheme().palette.mode === 'dark';
  const isMobile   = useMediaQuery('(max-width:768px)');
  const sidebarW   = useSidebarWidth();

  const [userlogin,    setUserlogin]    = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [datefrom,     setDatefrom]     = useState(null);
  const [dateto,       setDateto]       = useState(null);
  const [chartData,    setChartData]    = useState([]);
  const [salesMeta,    setSalesMeta]    = useState(null);  // { name, total, range }
  const [salesRaw,     setSalesRaw]     = useState([]);
  const [queried,      setQueried]      = useState(false);

  /* ── user ── */
  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const userEmail = useMemo(() => userlogin?.data?.email || null, [userlogin]);

  /* ── fetch sales list ── */
  useEffect(() => {
    if (userEmail) dispatch(Get_All_Sales_By_TeamLeader_Email_Action(userEmail));
  }, [userEmail, dispatch]);

  const resGetAllsales = useSelector(s => s.AllSales.salesforteamleaders);
  const Allsales       = Array.isArray(resGetAllsales?.data) ? resGetAllsales.data : [];

  const usersArr = useSelector(s => s.AllSales.Sales_For_Selected_teamLeader);
  const usersarray = Array.isArray(usersArr?.data) ? usersArr.data : [];

  useEffect(() => {
    if (usersarray.length > 0) setSalesRaw(usersarray);
  }, [usersarray]);

  /* ── filter by date ── */
  const filterByDate = (leads, from, to) => {
    if (!from && !to) return leads;
    return leads.filter(l => {
      const d = new Date(l.createdAt).toISOString().split('T')[0];
      if (from && to)  return d >= from && d <= to;
      if (from)        return d >= from;
      if (to)          return d <= to;
      return true;
    });
  };

  /* ── get data ── */
  const handleGetData = () => {
    if (!selectedSale) return;
    const found = salesRaw.find(s => s.salesID === selectedSale._id);
    if (!found) return;

    const stages = found.stages.map((stage, i) => {
      const filtered = filterByDate(stage.leads, datefrom, dateto);
      return { name: stage.stageName, leads: filtered.length, fill: STAGE_COLORS[i % STAGE_COLORS.length] };
    }).filter(s => s.leads > 0);

    const total = stages.reduce((s, d) => s + d.leads, 0);
    setChartData(stages);
    setSalesMeta({
      name:  selectedSale.name,
      total,
      range: datefrom && dateto ? `${datefrom} → ${dateto}` : datefrom ? `From ${datefrom}` : dateto ? `Until ${dateto}` : 'All time',
    });
    setQueried(true);
  };

  /* ── inline CSS ── */
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
    @keyframes sfade { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
    .sf-bar:hover { opacity: 0.88; transition: opacity 0.15s; }
  `;

  return (
    <>
      <style>{css}</style>
      <div style={{
        marginLeft: isMobile ? SIDEBAR_COLLAPSED : sidebarW,
        marginTop: 64,
        minHeight: 'calc(100vh - 64px)',
        background: isDark ? '#111122' : C.bg,
        fontFamily: "'DM Sans', sans-serif",
        transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{ padding: isMobile ? '16px' : '28px 28px 60px' }}>

          {/* ── Breadcrumb ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 20 }}>
            <Link to="/AllLeadsForTeamLeader" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Home</Link>
            <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
            <Link to="/TeamLeaderMysales" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
              Sales Team — {userlogin?.data?.name}
            </Link>
            <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
            <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Sales Report</span>
          </div>

          {/* ── Page title ── */}
          <div style={{ marginBottom: 24 }}>
            <p style={{ margin: '0 0 3px', fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Analytics</p>
            <h1 style={{ margin: 0, fontSize: isMobile ? 26 : 34, fontWeight: 800, color: isDark ? '#e0e0ff' : C.text, letterSpacing: '-0.4px', lineHeight: 1.15 }}>
              Sales Stage<br />Report
            </h1>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: C.textMuted }}>
              Choose a sales person and date range to visualise stage performance.
            </p>
          </div>

          {/* ── Filter Card ── */}
          <div style={{ background: isDark ? '#1a1a2e' : C.white, border: `1px solid ${isDark ? '#2a2a45' : C.border}`, borderRadius: 16, padding: isMobile ? 18 : 28, boxShadow: C.shadowMd, marginBottom: 24, animation: 'sfade 0.3s both' }}>

            <p style={{ margin: '0 0 18px', fontSize: 13, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Filter Options</p>

            {/* Sales picker */}
            <div style={{ marginBottom: 20 }}>
              <Autocomplete
                options={Allsales}
                getOptionLabel={o => o.name || ''}
                onChange={(_, v) => setSelectedSale(v)}
                renderInput={params => (
                  <TextField {...params} label="Sales Person *" size="small" sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '10px', '& fieldset': { borderColor: C.border }, '&:hover fieldset': { borderColor: C.accent }, '&.Mui-focused fieldset': { borderColor: C.accent, borderWidth: 1 } },
                    '& .MuiInputLabel-root.Mui-focused': { color: C.accent },
                  }} />
                )}
              />
            </div>

            {/* Date range */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={datefrom ? dayjs(datefrom) : null}
                  onChange={v => setDatefrom(v ? v.format('YYYY-MM-DD') : null)}
                  slotProps={{ textField: { size: 'small', sx: dpSx } }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={dateto ? dayjs(dateto) : null}
                  onChange={v => setDateto(v ? v.format('YYYY-MM-DD') : null)}
                  slotProps={{ textField: { size: 'small', sx: dpSx } }}
                />
              </LocalizationProvider>
            </div>

            {/* CTA */}
            <button
              onClick={handleGetData}
              disabled={!selectedSale}
              style={{
                width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
                background: selectedSale ? `linear-gradient(135deg, ${C.accent}, ${C.accentDark})` : '#E2E8F0',
                color: selectedSale ? '#fff' : '#94A3B8',
                fontSize: 14, fontWeight: 700, cursor: selectedSale ? 'pointer' : 'not-allowed',
                fontFamily: "'DM Sans',sans-serif",
                boxShadow: selectedSale ? '0 4px 12px rgba(13,71,161,0.3)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              Generate Report
            </button>
          </div>

          {/* ── Results ── */}
          {queried && salesMeta && (
            <div style={{ animation: 'sfade 0.35s both' }}>

              {/* Summary strip */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <MiniStat label="Sales Person"  value={salesMeta.name}  color={C.accent}  />
                <MiniStat label="Total Leads"   value={salesMeta.total} color={C.warning} />
                <MiniStat label="Active Stages" value={chartData.length} />
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', boxShadow: C.shadow, minWidth: 180 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Date Range</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{salesMeta.range}</div>
                </div>
              </div>

              {chartData.length === 0 ? (
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 16, padding: '48px 20px', textAlign: 'center', color: C.textMuted, boxShadow: C.shadow }}>
                  <p style={{ fontSize: 14, margin: 0 }}>No leads found for this sales person in the selected period.</p>
                </div>
              ) : (
                <>
                  {/* ── Bar Chart ── */}
                  <div style={{ background: isDark ? '#1a1a2e' : C.white, border: `1px solid ${isDark ? '#2a2a45' : C.border}`, borderRadius: 16, padding: isMobile ? '18px 10px 12px' : '24px 24px 16px', boxShadow: C.shadowMd, marginBottom: 20 }}>
                    <div style={{ marginBottom: 20 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Breakdown</p>
                      <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: isDark ? '#e0e0ff' : C.text }}>
                        Leads per Stage — <span style={{ color: C.accent }}>{salesMeta.name}</span>
                      </h3>
                    </div>

                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={chartData} barCategoryGap="35%" margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F2F8" vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 11, fill: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}
                          axisLine={false} tickLine={false}
                          angle={-35} textAnchor="end" height={70}
                          interval={0}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: C.textMuted, fontFamily: "'DM Sans',sans-serif" }}
                          axisLine={false} tickLine={false} allowDecimals={false}
                        />
                        <ReTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(13,71,161,0.04)' }} />
                        <Bar dataKey="leads" radius={[6, 6, 0, 0]} maxBarSize={56}>
                          {chartData.map((d, i) => (
                            <Cell key={i} fill={d.fill} className="sf-bar" />
                          ))}
                          <LabelList dataKey="leads" position="top" style={{ fontSize: 12, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", fill: isDark ? '#e0e0ff' : C.text }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* ── Stage pills ── */}
                  <div style={{ background: isDark ? '#1a1a2e' : C.white, border: `1px solid ${isDark ? '#2a2a45' : C.border}`, borderRadius: 16, padding: isMobile ? 16 : 22, boxShadow: C.shadow }}>
                    <p style={{ margin: '0 0 14px', fontSize: 10, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Stage Breakdown</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {chartData.map((d, i) => {
                        const pct = salesMeta.total > 0 ? Math.round((d.leads / salesMeta.total) * 100) : 0;
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: isDark ? '#252540' : C.bg, border: `1.5px solid ${isDark ? '#2a2a45' : C.border}`, borderRadius: 10, padding: '10px 14px', flex: '1 1 200px' }}>
                            <div style={{ width: 10, height: 10, borderRadius: 3, background: d.fill, flexShrink: 0 }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#e0e0ff' : C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                            <span style={{ fontSize: 16, fontWeight: 800, color: d.fill }}>{d.leads}</span>
                            <span style={{ fontSize: 11, color: C.textMuted }}>({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
};
